'use client'

import { useRouter } from 'next/navigation'
import { useEffect } from 'react'

import { Text } from '@wartek-id/text'

import {
  trackClickApplyFilters,
  trackViewDataIndukNasional,
} from '@/utils/tracker/modules/datainduk'
import { TrackerSection, TrackerSubSection } from '@/utils/tracker/shared'
import {
  useFetchDataIndukFilters,
  useFetchSatpenStats,
  useFetchSatpenStatsByParentWilayah,
} from '@/apis/useDataInduk'

import { TitlePage } from '@/modules/DataInduk/components/TitlePage'

import { Container } from '@/components/Container'

import { DATA_INDUK_ROUTE } from '@/configs/constants'

import { DataIndukSearchBox } from '../DataIndukSearchBox'
import { ApplyFilterButton } from '../Filter/ApplyFilterButton'
import { FilterBentuk } from '../Filter/FilterBentuk'
import { FilterJalur } from '../Filter/FilterJalur'
import { FilterJenis } from '../Filter/FilterJenis'
import { FilterSection } from '../Filter/FilterSection'
import { FilterStatus } from '../Filter/FilterStatus'
import { JenjangStats } from '../JenjangStats/JenjangStats'
import { JenjangStatsLoader } from '../JenjangStats/JenjangStatsLoader'
import { ListProvinceContainer } from '../ProvinceLogoCard/ListProvinceContainer'
import { DATA_INDUK_TITLE_PREFIX, VARIANT } from '../../constant'
import { statsToTableArray } from '../../helpers'
import { useFilterState } from '../../hooks/useFilterState'
import {
  KODE_WILAYAH_NASIONAL,
  KODE_WILAYAH_NASIONAL_FOR_TRACKER,
} from '../../provinces'

export const PaudDikdasmenPanel = () => {
  const router = useRouter()

  const {
    queryBentuk,
    queryJalur,
    queryStatus,
    queryJenis,

    selectedJalur,
    selectedBentuk,
    selectedStatus,
    selectedJenis,

    handleChangeJalur,
    handleChangeBentuk,
    handleChangeStatus,
    handleChangeJenis,

    getFilterSearchParams,
    getFilterSearchParamsFromQuery,
  } = useFilterState({
    variant: VARIANT.SATPEN,
    view: 'list',
  })

  const { data: dataStatsByParent, isPending: isPendingStatsByParent } =
    useFetchSatpenStatsByParentWilayah(
      KODE_WILAYAH_NASIONAL,
      {
        bentuk: queryBentuk,
        jalur: queryJalur,
        status: queryStatus,
        jenis: queryJenis,
      },
      {
        enabled: true,
      }
    )

  const { data: dataFilters, isPending: isPendingFilters } =
    useFetchDataIndukFilters({
      enabled: true,
    })

  const { data: dataStats, isPending: isPendingStats } = useFetchSatpenStats(
    KODE_WILAYAH_NASIONAL,
    {
      enabled: true,
    }
  )

  const handleFilterSubmission = () => {
    trackClickApplyFilters({
      locationLevel: 'Nasional',
      locationId: KODE_WILAYAH_NASIONAL_FOR_TRACKER,
      jalur: selectedJalur,
      bentuk: selectedBentuk,
      status: selectedStatus,
      jenis: selectedJenis,
      section: TrackerSection.SATPEN,
      subSection: TrackerSubSection.PAUD_DIKDASMEN,
    })

    const searchParams = getFilterSearchParams({})
    router.replace(searchParams, { scroll: false })
  }

  useEffect(() => {
    trackViewDataIndukNasional({
      section: TrackerSection.SATPEN,
      subSection: TrackerSubSection.PAUD_DIKDASMEN,
    })
  }, [])

  return (
    <div>
      <section id="tab-title-section" className="bg-white py-4">
        <Container>
          <Text variant="heading-sm" className="mb-2">
            Data Berdasarkan Pendidikan Anak Usia Dini, Pendidikan Dasar, dan
            Pendidikan Menengah
          </Text>
          <Text>
            Halaman ini berisi referensi data induk Satuan Pendidikan untuk
            Pendidikan Anak Usia Dini, Pendidikan Dasar, dan Pendidikan
            Menengah. Pengguna dapat mencari data nama sekolah melalui kotak
            pencarian (search bar) dengan mengisi nama sekolah ataupun melihat
            jumlah Satuan Pendidikan di setiap daerah.
          </Text>
        </Container>
      </section>

      <section id="search-section" className="flex flex-col">
        <DataIndukSearchBox
          source={DATA_INDUK_ROUTE}
          locationId={`${KODE_WILAYAH_NASIONAL_FOR_TRACKER}`}
          locationLevel="Nasional"
        />
      </section>

      <section id="stats-section" className="bg-white pt-4">
        <Container>
          <div className="w-full rounded-lg bg-blue-90 p-4">
            <Text variant="action" color="inverse">
              {DATA_INDUK_TITLE_PREFIX[VARIANT.SATPEN]}
            </Text>
            {isPendingStats ? (
              <JenjangStatsLoader />
            ) : (
              <JenjangStats
                items={
                  dataStats
                    ? statsToTableArray(
                        dataStats?.satuanPendidikanStatistics,
                        'satuan-pendidikan'
                      )
                    : []
                }
              />
            )}
          </div>
        </Container>
      </section>

      <section id="title-section" className="bg-white pt-4">
        <Container>
          <TitlePage variant={VARIANT.SATPEN} />

          {!isPendingFilters && dataFilters && (
            <div className="mb-4 flex w-full flex-col lg:flex-row lg:items-center lg:justify-between">
              <FilterSection
                FilterJalur={
                  <FilterJalur
                    value={selectedJalur}
                    onChange={handleChangeJalur}
                    loadingStatus={'success'}
                    jalur={dataFilters?.filters?.jalurPendidikan}
                  />
                }
                FilterBentuk={
                  <FilterBentuk
                    value={selectedBentuk}
                    onChange={handleChangeBentuk}
                    loadingStatus={'success'}
                    bentuk={dataFilters?.filters?.bentukPendidikanSuperGroup}
                  />
                }
                FilterStatus={
                  <FilterStatus
                    value={selectedStatus}
                    onChange={handleChangeStatus}
                    loadingStatus={'success'}
                    status={dataFilters?.filters?.statusSatuanPendidikan}
                  />
                }
                FilterJenis={
                  <FilterJenis
                    value={selectedJenis}
                    onChange={handleChangeJenis}
                    loadingStatus={'success'}
                    jenis={dataFilters?.filters?.jenisPendidikan}
                  />
                }
                ApplyButton={
                  <ApplyFilterButton onClick={handleFilterSubmission} />
                }
              />
              <div className="flex w-full items-center justify-end gap-2 md:w-[400px]"></div>
            </div>
          )}
        </Container>
      </section>

      <ListProvinceContainer
        provinceId=""
        isPending={isPendingStatsByParent}
        data={dataStatsByParent}
        additionalParams={getFilterSearchParamsFromQuery()}
        prefixPath={DATA_INDUK_ROUTE}
        variant={VARIANT.SATPEN}
      />
    </div>
  )
}
